/**
 * saas-org.ts — Team/seats + account-settings surfaces of the SaaS API
 *
 * Purpose:    Typed client + wire mapping for /v1/team (list/invite/remove —
 *             documented on the gateway, rolling out) and the account surface
 *             (/v1/me profile, password change, GDPR data export, account
 *             delete). 404/405/501 pass through so pages render "coming
 *             online" via isComingOnline, never a crash.
 * Inputs:     request/withMockFallback/mapResult from ./saas-client
 * Outputs:    teamApi / accountApi returning Result<T, SentryError>
 * Constraints: Enveloped snake_case wire; tolerant mappers. Mock data is
 *              static module state for VITE_API_MOCK/offline demo.
 * SOT:        nsentry SaaS completion spec § G-WEB (team, account, legal)
 */
import { ok, err } from './result'
import type { SentryError } from './sentry-error'
import { request, withMockFallback, mapResult } from './saas-client'

// ─── View models ─────────────────────────────────────────────────────────────

export interface TeamMember {
  id: string
  email: string
  name: string | null
  role: 'owner' | 'member'
  status: 'active' | 'invited'
  joinedAt: string | null
}

export interface AccountProfile {
  tenantId: string | null
  email: string
  name: string | null
  tier: string | null
}

// ─── Wire shapes (enveloped snake_case) ──────────────────────────────────────

export interface WireTeamMember {
  id: string
  email: string
  name?: string | null
  role?: string
  status?: string
  joined_at?: string | null
}

export interface WireMe {
  tenant_id?: string | null
  email?: string
  name?: string | null
  tier?: string | null
}

// ─── Mappers ─────────────────────────────────────────────────────────────────

export function mapTeamMember(w: WireTeamMember): TeamMember {
  return {
    id: w.id,
    email: w.email,
    name: w.name ?? null,
    role: w.role === 'owner' ? 'owner' : 'member',
    status: w.status === 'invited' ? 'invited' : 'active',
    joinedAt: w.joined_at ?? null,
  }
}

export function mapTeam(body: { members?: WireTeamMember[] }): TeamMember[] {
  return (body.members ?? []).map(mapTeamMember)
}

export function mapMe(body: { me?: WireMe }): AccountProfile {
  return {
    tenantId: body.me?.tenant_id ?? null,
    email: body.me?.email ?? '',
    name: body.me?.name ?? null,
    tier: body.me?.tier ?? null,
  }
}

// ─── Mock state (demo/offline only) ──────────────────────────────────────────

const mockMembers: TeamMember[] = [
  { id: 'u-1', email: 'owner@example.com', name: 'Workspace Owner', role: 'owner', status: 'active', joinedAt: new Date().toISOString() },
]

let mockProfile: AccountProfile = {
  tenantId: 'demo-tenant',
  email: 'owner@example.com',
  name: 'Workspace Owner',
  tier: 'free',
}

/**
 * Test-only reset of the module-level mock team/profile state. Production
 * code never calls this — it exists so unit tests can start each case from
 * a fresh seed without needing a full module re-import (matching the
 * __resetMockStoreForTests convention in saas-mock.ts).
 */
export function __resetMockOrgStateForTests(): void {
  mockMembers.length = 0
  mockMembers.push({ id: 'u-1', email: 'owner@example.com', name: 'Workspace Owner', role: 'owner', status: 'active', joinedAt: new Date().toISOString() })
  mockProfile = {
    tenantId: 'demo-tenant',
    email: 'owner@example.com',
    name: 'Workspace Owner',
    tier: 'free',
  }
}

// ─── APIs ────────────────────────────────────────────────────────────────────

export const teamApi = {
  list: () =>
    withMockFallback(
      async () => mapResult(await request<{ members?: WireTeamMember[] }>('/v1/team'), mapTeam),
      () => ok([...mockMembers]),
    ),
  invite: (input: { email: string; role: 'member' | 'owner' }) =>
    withMockFallback(
      async () =>
        mapResult(
          await request<{ member: WireTeamMember }>('/v1/team/invites', {
            method: 'POST',
            body: JSON.stringify(input),
          }),
          (body) => mapTeamMember(body.member),
        ),
      () => {
        if (mockMembers.some((m) => m.email === input.email)) {
          return err<SentryError>({ type: 'config_error', message: 'That email is already on the team' })
        }
        const member: TeamMember = {
          id: `u-${mockMembers.length + 1}`,
          email: input.email,
          name: null,
          role: input.role,
          status: 'invited',
          joinedAt: null,
        }
        mockMembers.push(member)
        return ok({ ...member })
      },
    ),
  remove: (id: string) =>
    withMockFallback(
      () => request<void>(`/v1/team/members/${id}`, { method: 'DELETE' }),
      () => {
        const idx = mockMembers.findIndex((m) => m.id === id)
        if (idx === -1) return err({ type: 'config_error', message: 'Member not found' })
        if (mockMembers[idx].role === 'owner') return err({ type: 'config_error', message: 'The owner cannot be removed' })
        mockMembers.splice(idx, 1)
        return ok(undefined)
      },
    ),
}

export const accountApi = {
  me: () =>
    withMockFallback(
      async () => mapResult(await request<{ me?: WireMe }>('/v1/me'), mapMe),
      () => ok({ ...mockProfile }),
    ),
  update: (patch: { name?: string; email?: string }) =>
    withMockFallback(
      async () =>
        mapResult(
          await request<{ me?: WireMe }>('/v1/me', { method: 'PATCH', body: JSON.stringify(patch) }),
          mapMe,
        ),
      () => {
        mockProfile = { ...mockProfile, ...patch }
        return ok({ ...mockProfile })
      },
    ),
  changePassword: (input: { currentPassword: string; newPassword: string }) =>
    withMockFallback(
      () =>
        request<void>('/v1/me/password', {
          method: 'POST',
          body: JSON.stringify({ current_password: input.currentPassword, new_password: input.newPassword }),
        }),
      () => ok(undefined),
    ),
  /** GDPR data export — the full tenant-scoped dataset as JSON. */
  exportData: () =>
    withMockFallback(
      () => request<Record<string, unknown>>('/v1/me/export'),
      () => ok({ profile: mockProfile, note: 'Demo export — live data arrives from /v1/me/export.' } as Record<string, unknown>),
    ),
  /** GDPR right-to-erasure — permanently deletes the tenant and all its data. */
  deleteAccount: (confirmEmail: string) =>
    withMockFallback(
      () =>
        request<void>('/v1/me', {
          method: 'DELETE',
          body: JSON.stringify({ confirm_email: confirmEmail }),
        }),
      () => ok(undefined),
    ),
}

/** Trigger a browser download of the export payload as a JSON file. */
export function downloadJson(data: unknown, filename: string): void {
  const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' })
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = filename
  a.click()
  URL.revokeObjectURL(url)
}
